import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../classes/appApi';
import { useOffline } from '../components/App/AppOfflineContext';
import MainLayout, { useMainLayoutContext } from '../components/MainLayout';
import {
    IdNameOptionModel,
    IncidentCreateRequestModel,
    IncidentCreateResponseModel,
    IncidentCreationOptionsModel,
    OperatingUnitSummaryModel,
} from '../defs/defs';
import ContinuumFormList from '../shared/components/ContinuumFormList';
import EqxCard from '../shared/components/EqxCard/EqxCard';
import EqxError from '../shared/components/EqxError';
import EqxForm, {
    EqxFormInputDateTime,
    EqxFormInputRadioButtons,
    EqxFormInputSelect,
    EqxFormInputText,
    EqxFormInputTextarea,
    EqxFormRow,
    EqxFormSubmit,
    useFormState,
} from '../shared/components/EqxForm/EqxForm';
import EqxIcon from '../shared/components/EqxIcon';
import EqxLoading from '../shared/components/EqxLoading';
import useApiEndpoint from '../shared/hooks/ApiEndpoint';

const toIdNameSelectOptions = (items: IdNameOptionModel[]) =>
    items
        .map((o) => {
            const n = Number(o.id);
            const value = Number.isInteger(n) ? String(n) : '';
            return { value, label: o.name };
        })
        .filter((opt) => opt.value !== '');

const toOperatingUnitSelectOptions = (items: OperatingUnitSummaryModel[]) =>
    items
        .map((o) => {
            const n = Number(o.id);
            const value = Number.isInteger(n) ? String(n) : '';
            const label = o.fullPath.trim() ? o.fullPath : o.name;
            return { value, label };
        })
        .filter((opt) => opt.value !== '');

const localDateTimeDefault = () => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

const isMissingRequiredText = (s: string) => !s.trim();

/** Integer ID required (includes root IDs such as -1; NaN would JSON-serialize badly). */
const isValidRequiredId = (s: string) => {
    if (s === '') return false;
    const n = Number(s);
    return Number.isInteger(n);
};

const validateIncidentCreate = (data: Record<string, string>) => {
    const errors: Record<string, string> = {};
    if (isMissingRequiredText(data.detail)) errors.detail = 'Please enter an incident description.';
    if (!isValidRequiredId(data.incidentTypeOperationID)) errors.incidentTypeOperationID = 'Please select an incident type.';
    if (isMissingRequiredText(data.title)) errors.title = 'Please give this incident report a title.';
    if (!isValidRequiredId(data.businessFunctionID)) errors.businessFunctionID = 'Please select a business function.';
    if (!isValidRequiredId(data.operatingUnitID)) errors.operatingUnitID = 'Please select an operating unit.';
    if (!isValidRequiredId(data.businessProcessOperationID)) {
        errors.businessProcessOperationID = 'Please select the business process or operation.';
    }
    if (!isValidRequiredId(data.sourceID)) errors.sourceID = 'Please select how the incident was discovered.';
    if (!data.startedTimestamp) errors.startedTimestamp = 'Please specify when the incident occurred.';
    if (data.locationType === 'site' && !isValidRequiredId(data.locationID)) errors.locationID = 'Please select a site location.';
    if (data.locationType === 'misc' && isMissingRequiredText(data.locationDescription)) {
        errors.locationDescription = 'Please enter the location.';
    }
    return errors;
};

const prepareIncidentCreatePayload = (data: Record<string, string>): IncidentCreateRequestModel => {
    const locationType = data.locationType === 'misc' ? 'misc' : 'site';
    return {
        detail: data.detail,
        isNearMiss: data.isNearMiss === 'true',
        incidentTypeOperationID: Number.parseInt(data.incidentTypeOperationID, 10),
        title: data.title,
        businessFunctionID: Number.parseInt(data.businessFunctionID, 10),
        operatingUnitID: Number.parseInt(data.operatingUnitID, 10),
        businessProcessOperationID: Number.parseInt(data.businessProcessOperationID, 10),
        sourceID: Number.parseInt(data.sourceID, 10),
        startedTimestamp: new Date(data.startedTimestamp).toISOString(),
        locationType,
        locationID: locationType === 'site' ? Number.parseInt(data.locationID, 10) : undefined,
        locationDescription: locationType === 'misc' ? data.locationDescription : undefined,
    };
};

interface IncidentCreateFormProps {
    formState: ReturnType<typeof useFormState>;
    options: IncidentCreationOptionsModel;
    onSuccess: (response: IncidentCreateResponseModel) => void;
}

const IncidentCreateForm: React.FC<IncidentCreateFormProps> = ({ formState, options, onSuccess }) => {
    const api = useApi();
    const formData = formState.data;

    return (
        <EqxForm
            api={api}
            state={formState}
            action={api.endpoints.createIncident().url}
            method="post"
            validate={(d) => validateIncidentCreate(d as Record<string, string>)}
            prepareData={(d) => prepareIncidentCreatePayload(d as Record<string, string>)}
            onSuccess={onSuccess}
        >
            <EqxFormRow
                name="detail"
                label="Describe what happened"
                fullWidth={true}
                comment="This field is intended to capture the facts of what happened, rather than why it happened."
            >
                <EqxFormInputTextarea
                    name="detail"
                    placeholder="Describe the facts of what happened..."
                    required={true}
                />
            </EqxFormRow>

            <EqxFormRow
                name="isNearMiss"
                label="Was this a near miss?"
                comment='Incidents where no consequential cost or injury occurs are referred to as a "near miss".'
            >
                <EqxFormInputRadioButtons
                    name="isNearMiss"
                    required={true}
                    options={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                    ]}
                />
            </EqxFormRow>

            <EqxFormRow
                name="incidentTypeOperationID"
                label="What type of incident are you creating?"
                comment="Incident type (operation)"
            >
                <EqxFormInputSelect
                    name="incidentTypeOperationID"
                    required={true}
                    options={toIdNameSelectOptions(options.incidentTypeOperations)}
                    blankText="Select incident type"
                />
            </EqxFormRow>

            <EqxFormRow name="title" label="Give this incident report a title">
                <EqxFormInputText
                    name="title"
                    required={true}
                    placeholder="e.g. Slip in warehouse aisle 3"
                />
            </EqxFormRow>

            <EqxFormRow name="businessFunctionID" label="Business function">
                <EqxFormInputSelect
                    name="businessFunctionID"
                    required={true}
                    options={toIdNameSelectOptions(options.businessFunctions)}
                    blankText="Select business function"
                />
            </EqxFormRow>

            <EqxFormRow name="operatingUnitID" label="Operating unit">
                <EqxFormInputSelect
                    name="operatingUnitID"
                    required={true}
                    options={toOperatingUnitSelectOptions(options.operatingUnits)}
                    blankText="Select operating unit"
                />
            </EqxFormRow>

            <EqxFormRow
                name="businessProcessOperationID"
                label="What business process or operation was being performed when the incident occurred?"
            >
                <EqxFormInputSelect
                    name="businessProcessOperationID"
                    required={true}
                    options={toIdNameSelectOptions(options.businessProcessOperations)}
                    blankText="Select operation"
                />
            </EqxFormRow>

            <EqxFormRow name="sourceID" label="How was the incident discovered?">
                <EqxFormInputSelect
                    name="sourceID"
                    required={true}
                    options={toIdNameSelectOptions(options.sources)}
                    blankText="Select source"
                />
            </EqxFormRow>

            <EqxFormRow name="startedTimestamp" label="When did the incident occur?">
                <EqxFormInputDateTime name="startedTimestamp" required={true} />
            </EqxFormRow>

            <EqxFormRow name="locationType" label="Where did the incident occur?">
                <EqxFormInputRadioButtons
                    name="locationType"
                    required={true}
                    options={[
                        { value: 'site', label: 'Site Location' },
                        { value: 'misc', label: 'Misc Location' },
                    ]}
                />
            </EqxFormRow>

            {formData.locationType === 'site' && (
                <EqxFormRow name="locationID" label="Select site location">
                    <EqxFormInputSelect
                        name="locationID"
                        required={true}
                        options={toIdNameSelectOptions(options.locations)}
                        blankText="Select location"
                    />
                </EqxFormRow>
            )}

            {formData.locationType === 'misc' && (
                <EqxFormRow name="locationDescription" label="Enter location">
                    <EqxFormInputText name="locationDescription" placeholder="Describe the location" required={true} />
                </EqxFormRow>
            )}

            <EqxFormSubmit label="Create incident" variant="success" className="w-100" size="lg" />
        </EqxForm>
    );
};

const IncidentCreateContent: React.FC = () => {
    const api = useApi();
    const navigate = useNavigate();
    const { isOffline } = useOffline();
    const mainContext = useMainLayoutContext();
    const optionsRequest = useApiEndpoint<IncidentCreationOptionsModel>(api, api.endpoints.incidentCreationOptions());
    const { response: options, loading, error } = optionsRequest;
    const [created, setCreated] = useState<IncidentCreateResponseModel | null>(null);

    const formState = useFormState(undefined, {
        data: {
            detail: '',
            isNearMiss: 'false',
            incidentTypeOperationID: '',
            title: '',
            businessFunctionID: '',
            operatingUnitID: '',
            businessProcessOperationID: '',
            sourceID: '',
            startedTimestamp: localDateTimeDefault(),
            locationType: 'site',
            locationID: '',
            locationDescription: '',
        },
    });

    if (!mainContext.user) return <EqxLoading />;
    if (!mainContext.user.staffID) return <EqxError error="Only staff can create incidents." />;
    if (!mainContext.permissions?.canCreateIncident)
        return <EqxError error="Your user does not have permission to create incidents. Please contact the support team." />;

    const handleFormSuccess = (response: IncidentCreateResponseModel) => {
        setCreated(response);
    };

    return (
        <>
            {!created && (
                <EqxCard
                    isSection={true}
                    title="Create incident"
                    disabled={isOffline}
                    disabledMessage={<><EqxIcon fa="fas fa-wifi " /> Unavailable Offline</>}
                >
                    {loading && <EqxLoading />}
                    {!loading && error && <EqxError error={error} />}
                    {!loading && !error && options && (
                        <IncidentCreateForm formState={formState} options={options} onSuccess={handleFormSuccess} />
                    )}
                </EqxCard>
            )}
            {created && (
                <EqxCard isSection={true} title="Incident created">
                    <div className="alert alert-success mb-3">
                        Your incident has been logged successfully.
                    </div>
                    <div className="mb-4">
                        <div className="text-muted small text-uppercase mb-1">Incident ID</div>
                        <div className="fs-4 fw-semibold font-monospace">{created.taskID}</div>
                        <p className="text-muted small mb-0 mt-2">
                            Use this reference if you need to follow up on this incident.
                        </p>
                    </div>
                    {created.continuumForms && created.continuumForms.length > 0 ? (
                        <div className="mb-4">
                            <h6 className="mb-2">Continuum Forms</h6>
                            <p className="text-muted small mb-3">
                                There are follow-up forms for incidents of this type:
                            </p>
                            <ContinuumFormList
                                forms={created.continuumForms}
                                taskID={created.taskID}
                                useModal={false}
                                openInNewTab={true}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                    <button type="button" className="btn btn-primary btn-lg w-100" onClick={() => navigate('/work')}>
                        Back to Work
                    </button>
                </EqxCard>
            )}
        </>
    );
};

const PageIncidentCreate: React.FC = () => (
    <MainLayout pageTitle="Create Incident" relatedLinks={[{ text: 'Work', url: '/work' }]}>
        <IncidentCreateContent />
    </MainLayout>
);

export default PageIncidentCreate;
